/**
 * AEO Schema Builder
 * Specialized schema builders for Answer Engine Optimization
 */

import { CORE_BUSINESS } from './coreSchemas';

interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface ListItem {
  position: number;
  name: string;
  description: string;
  url: string;
  image?: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Build HowTo Schema for process pages
 */
export const buildHowToSchema = (
  name: string,
  description: string,
  steps: HowToStep[],
  totalTime?: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name,
  description,
  totalTime: totalTime || 'PT1H',
  step: steps.map((step, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: step.name,
    text: step.text,
    url: step.url ? `${CORE_BUSINESS.url}${step.url}` : undefined,
  })),
});

/**
 * Build Speakable Schema for voice search optimization
 */
export const buildSpeakableSchema = (
  pathname: string,
  cssSelectors: string[]
) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  url: `${CORE_BUSINESS.url}${pathname}`,
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: cssSelectors,
  },
});

/**
 * Build Enhanced FAQ Schema with author attribution
 */
export const buildEnhancedFAQSchema = (
  items: FAQItem[],
  author?: {
    name: string;
    url: string;
  }
) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
      author: author
        ? {
            '@type': 'Organization',
            name: author.name,
            url: author.url,
          }
        : {
            '@type': 'Organization',
            name: CORE_BUSINESS.name,
            url: CORE_BUSINESS.url,
          },
      datePublished: new Date().toISOString().split('T')[0],
    },
  })),
});

/**
 * Build ItemList Schema for "Best Of" pages
 */
export const buildItemListSchema = (
  name: string,
  description: string,
  items: ListItem[]
) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  description,
  numberOfItems: items.length,
  itemListElement: items.map((item) => ({
    '@type': 'ListItem',
    position: item.position,
    name: item.name,
    description: item.description,
    url: `${CORE_BUSINESS.url}${item.url}`,
    image: item.image,
  })),
});

/**
 * Build BreadcrumbList Schema
 */
export const buildBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${CORE_BUSINESS.url}${item.url}`,
  })),
});

/**
 * Build Article Schema with AEO enhancements
 */
export const buildArticleSchema = (
  headline: string,
  description: string,
  pathname: string,
  datePublished: string,
  dateModified: string,
  imageUrl?: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  url: `${CORE_BUSINESS.url}${pathname}`,
  datePublished,
  dateModified,
  author: {
    '@type': 'Organization',
    name: CORE_BUSINESS.name,
    url: CORE_BUSINESS.url,
  },
  publisher: {
    '@type': 'Organization',
    name: CORE_BUSINESS.name,
    url: CORE_BUSINESS.url,
    logo: {
      '@type': 'ImageObject',
      url: CORE_BUSINESS.logo,
    },
  },
  image: imageUrl
    ? {
        '@type': 'ImageObject',
        url: imageUrl,
      }
    : undefined,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${CORE_BUSINESS.url}${pathname}`,
  },
});

/**
 * Build QAPage Schema for question-answer content
 */
export const buildQAPageSchema = (
  question: string,
  answer: string,
  pathname: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'QAPage',
  mainEntity: {
    '@type': 'Question',
    name: question,
    text: question,
    answerCount: 1,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
      author: {
        '@type': 'Organization',
        name: CORE_BUSINESS.name,
        url: CORE_BUSINESS.url,
      },
      dateCreated: new Date().toISOString().split('T')[0],
    },
  },
  url: `${CORE_BUSINESS.url}${pathname}`,
});

/**
 * Build CollectionPage Schema for category/listing pages
 */
export const buildCollectionPageSchema = (
  name: string,
  description: string,
  pathname: string,
  numberOfItems: number
) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name,
  description,
  url: `${CORE_BUSINESS.url}${pathname}`,
  mainEntity: {
    '@type': 'ItemList',
    numberOfItems,
  },
});

/**
 * Build VideoObject Schema for video content
 */
export const buildVideoSchema = (
  name: string,
  description: string,
  thumbnailUrl: string,
  uploadDate: string,
  duration?: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name,
  description,
  thumbnailUrl,
  uploadDate,
  duration: duration || 'PT5M',
  publisher: {
    '@type': 'Organization',
    name: CORE_BUSINESS.name,
    url: CORE_BUSINESS.url,
    logo: {
      '@type': 'ImageObject',
      url: CORE_BUSINESS.logo,
    },
  },
});

/**
 * Default speakable selectors for content pages
 */
export const DEFAULT_SPEAKABLE_SELECTORS = [
  '[data-speakable="summary"]',
  '[data-speakable="heading"]',
  '[data-speakable="content"]',
  'h1',
  'h2',
  '.article-summary',
  '.quick-answer',
  '.dealer-insight',
];

/**
 * Generate default author object
 */
export const DEFAULT_AUTHOR = {
  name: CORE_BUSINESS.name,
  url: CORE_BUSINESS.url,
};